import React, { useEffect, useRef } from 'react';
import { XIcon } from 'lucide-react';
import { useLanguage } from './i18n/LanguageContext';
export type LegalDocument = 'privacy' | 'terms';
interface LegalModalProps {
  legalDocument: LegalDocument | null;
  onClose: () => void;
}
export function LegalModal({
  legalDocument,
  onClose
}: LegalModalProps) {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const {
    t
  } = useLanguage();
  useEffect(() => {
    if (!legalDocument) {
      return undefined;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.document.addEventListener('keydown', handleKeyDown);
    closeButtonRef.current?.focus();
    return () => window.document.removeEventListener('keydown', handleKeyDown);
  }, [legalDocument, onClose]);
  if (!legalDocument) {
    return null;
  }
  const content = t.legal[legalDocument];
  const titleId = `${legalDocument}-policy-title`;
  return <div className="fixed inset-0 z-[110] flex items-end justify-center bg-dark/55 px-4 py-4 sm:items-center sm:p-6" role="presentation" onMouseDown={event => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  }} data-id="element-254" data-mp="1aqyamemkc7y8">
      <section role="dialog" aria-modal="true" aria-labelledby={titleId} className="max-h-[88svh] w-full max-w-2xl overflow-y-auto border border-gold/35 bg-warm-white shadow-[0_24px_70px_rgba(44,33,24,0.34)]" data-id="element-255" data-mp="1pnpccujplvr0">
        <div className="sticky top-0 flex items-start justify-between gap-5 border-b border-brand-border bg-warm-white px-6 py-5 sm:px-8" data-id="element-256" data-mp="wztl45cwsw1m">
          <div data-id="element-257" data-mp="11wa4ynd2so8w">
            <p className="font-body text-[10px] font-semibold uppercase tracking-[3px] text-gold" data-id="element-258" data-mp="16ol7lxz7ao55">
              Ginger Briones Studio
            </p>
            <h2 id={titleId} className="mt-1 font-serif text-3xl text-dark sm:text-4xl" data-id="element-259" data-mp="98xuvco3q8wh">
              {content.title}
            </h2>
            <p className="mt-1 font-body text-xs text-soft" data-id="element-260" data-mp="67urfmitlfe5">{t.legal.effective}</p>
          </div>
          <button ref={closeButtonRef} type="button" onClick={onClose} aria-label={t.legal.close} className="flex h-10 w-10 shrink-0 items-center justify-center border border-brand-border text-mid transition-colors hover:border-gold hover:text-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2" data-id="element-261" data-mp="18rkfwja4z3j">
            <XIcon className="h-5 w-5" strokeWidth={1.5} data-id="element-262" data-mp="3eft9l5749rwp" />
          </button>
        </div>

        <div className="space-y-7 px-6 py-7 sm:px-8 sm:py-9" data-id="element-263" data-mp="3tcao25alrmnh">
          <p className="font-body text-sm font-light leading-7 text-mid" data-id="element-264" data-mp="3tahccbl4uc8m">{t.legal.intro}</p>
          {content.sections.map(section => <section key={section.heading} data-id="element-265" data-mp="18ltm75udbbg4">
              <h3 className="font-serif text-2xl text-dark" data-id="element-266" data-mp="18ks1sargpclq">{section.heading}</h3>
              <p className="mt-2 font-body text-sm font-light leading-7 text-mid" data-id="element-267" data-mp="13mgofq1ykjyr">{section.body}</p>
            </section>)}
        </div>
      </section>
    </div>;
}